import {Collapse} from "@blueprintjs/core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {DragIcon} from "../../../../common/icons";
import {
  ETAEvaluationItemContainer,
  ETAEvaluationCollapseContainer,
  ETAEvaluationHeaderItemContainer,
  ETAEvaluationText,
  ETAEvaluationNameHeaderText,
  ETAEvaluationCollapseButton,
  ETAEvaluationDivider,
  ETAEvaluationContentContainer,
  ETAEvaluationContentSection,
  ETAEvaluationContentRow,
  HeaderText,
  SubHeaderText
} from "../common/ETANotificationConfigs.style";
import {ETANotificationConfigsFormValues, SendNotificationType} from "../ETANotificationConfigsProvider";
import {OptionType} from "../../../../common/types";
import {formatStopTypesForDisplay} from "../common/ETANotificationConfigs.utils";

type ETAEvaluationItemProps = {
  item: ETANotificationConfigsFormValues;
  rank: number;
  isExpanded: boolean;
  onToggleCollapse: () => void;
  onEdit: () => void;
  dragProps: any;
  includeOptimizationETA: boolean;
};

const renderAttributeContents = (options: OptionType[]): string => {
  if (options.length === 0) {
    return "all";
  }
  return options.map((option) => option.value).join(", ");
};

const formatNotificationVia = (type: SendNotificationType): string => {
  return type === SendNotificationType.EMAIL ? "Email" : "Text";
};

const formatTriggerDescription = (triggerWhen: string, awayFrom: string): string => {
  if (triggerWhen === "eta") {
    return `Trigger when ETA is ${awayFrom} min away from the current time`;
  } else {
    return `Trigger when Stop is ${awayFrom} stops away from the current stop`;
  }
};

const formatTimeWindow = (before: string, after: string): string => {
  return `${before} min before the ETA to ${after} min after the ETA`;
};

export const ETAEvaluationItem = ({
  item,
  rank,
  isExpanded,
  onToggleCollapse,
  onEdit,
  dragProps,
  includeOptimizationETA
}: ETAEvaluationItemProps) => {
  return (
    <ETAEvaluationItemContainer>
      <ETAEvaluationCollapseContainer isExpanded={isExpanded}>
        <ETAEvaluationHeaderItemContainer>
          <DragIcon {...dragProps} />
          <ETAEvaluationNameHeaderText ellipsize={true}>{item.name}</ETAEvaluationNameHeaderText>
        </ETAEvaluationHeaderItemContainer>
        <ETAEvaluationHeaderItemContainer>
          <ETAEvaluationText>Rank #{rank}</ETAEvaluationText>
          <ETAEvaluationCollapseButton onClick={onEdit}>
            <FontAwesomeIcon icon={solid("pencil")} color={"#6B7280"} />
          </ETAEvaluationCollapseButton>
          <ETAEvaluationCollapseButton onClick={onToggleCollapse}>
            <FontAwesomeIcon
              icon={isExpanded ? solid("chevron-up") : solid("chevron-down")}
              size="xs"
              color="#6B7280"
            />
          </ETAEvaluationCollapseButton>
        </ETAEvaluationHeaderItemContainer>
      </ETAEvaluationCollapseContainer>
      <Collapse isOpen={isExpanded}>
        <ETAEvaluationDivider />
        <ETAEvaluationContentContainer>
          <ETAEvaluationContentSection>
            <HeaderText fontSize="14px">Service Type</HeaderText>
            <ETAEvaluationContentRow>
              <SubHeaderText>{renderAttributeContents(item.attributes.serviceTypes)}</SubHeaderText>
            </ETAEvaluationContentRow>
          </ETAEvaluationContentSection>
          <ETAEvaluationContentSection>
            <HeaderText fontSize="14px">Stop Type</HeaderText>
            <ETAEvaluationContentRow>
              <SubHeaderText>
                {renderAttributeContents(formatStopTypesForDisplay(item.attributes.stopTypes))}
              </SubHeaderText>
            </ETAEvaluationContentRow>
          </ETAEvaluationContentSection>
          <ETAEvaluationContentSection>
            <HeaderText fontSize="14px">Customer</HeaderText>
            <ETAEvaluationContentRow>
              <SubHeaderText>{renderAttributeContents(item.attributes.customers)}</SubHeaderText>
            </ETAEvaluationContentRow>
          </ETAEvaluationContentSection>
          <ETAEvaluationContentSection>
            <HeaderText fontSize="14px">Customer Group</HeaderText>
            <ETAEvaluationContentRow>
              <SubHeaderText style={{whiteSpace: "pre-wrap"}}>
                {renderAttributeContents(item.attributes.customerGroups ?? [])}
              </SubHeaderText>
            </ETAEvaluationContentRow>
          </ETAEvaluationContentSection>
          <ETAEvaluationContentSection>
            <HeaderText fontSize="14px">Vehicle Type</HeaderText>
            <ETAEvaluationContentRow>
              <SubHeaderText>{renderAttributeContents(item.attributes.vehicleTypes)}</SubHeaderText>
            </ETAEvaluationContentRow>
          </ETAEvaluationContentSection>
          <ETAEvaluationContentSection>
            <HeaderText fontSize="14px">Priority</HeaderText>
            <ETAEvaluationContentRow>
              <SubHeaderText>{renderAttributeContents(item.attributes.priorities)}</SubHeaderText>
            </ETAEvaluationContentRow>
          </ETAEvaluationContentSection>
          <ETAEvaluationContentSection>
            <HeaderText fontSize="14px">Site</HeaderText>
            <ETAEvaluationContentRow>
              <SubHeaderText>{renderAttributeContents(item.attributes.sites)}</SubHeaderText>
            </ETAEvaluationContentRow>
          </ETAEvaluationContentSection>
          <ETAEvaluationContentSection>
            <HeaderText fontSize="14px">Driver</HeaderText>
            <ETAEvaluationContentRow>
              <SubHeaderText>{renderAttributeContents(item.attributes.drivers)}</SubHeaderText>
            </ETAEvaluationContentRow>
          </ETAEvaluationContentSection>
          <ETAEvaluationContentSection>
            <HeaderText fontSize="14px">Driver Group</HeaderText>
            <ETAEvaluationContentRow>
              <SubHeaderText>{renderAttributeContents(item.attributes.dispatchGroups ?? [])}</SubHeaderText>
            </ETAEvaluationContentRow>
          </ETAEvaluationContentSection>
          <ETAEvaluationContentSection>
            <HeaderText fontSize="14px">Notification Sent Via</HeaderText>
            <SubHeaderText>{formatNotificationVia(item.sendNotificationVia.type)}</SubHeaderText>
          </ETAEvaluationContentSection>

          {includeOptimizationETA && item.optimizationNotification?.enabled && (
            <ETAEvaluationContentSection flexDirection={"column"} gap={"0"}>
              <HeaderText fontSize="14px">Optimization Notification</HeaderText>
              <ETAEvaluationContentRow>
                <SubHeaderText>Trigger when the optimization is complete</SubHeaderText>
              </ETAEvaluationContentRow>
              <ETAEvaluationContentRow>
                <SubHeaderText>
                  Time Window:{" "}
                  {formatTimeWindow(
                    item.optimizationNotification.timeWindowBeforeETA,
                    item.optimizationNotification.timeWindowAfterETA
                  )}
                </SubHeaderText>
              </ETAEvaluationContentRow>
            </ETAEvaluationContentSection>
          )}

          <ETAEvaluationContentSection flexDirection={"column"} gap={"0"}>
            <HeaderText fontSize="14px">1st Notification</HeaderText>
            <ETAEvaluationContentRow>
              <SubHeaderText>
                {formatTriggerDescription(item.firstNotification.triggerWhen, item.firstNotification.awayFrom)}
              </SubHeaderText>
            </ETAEvaluationContentRow>
            <ETAEvaluationContentRow>
              <SubHeaderText>
                Time Window:{" "}
                {formatTimeWindow(
                  item.firstNotification.timeWindowBeforeETA,
                  item.firstNotification.timeWindowAfterETA
                )}
              </SubHeaderText>
            </ETAEvaluationContentRow>
          </ETAEvaluationContentSection>

          {item.secondNotification.enabled && (
            <ETAEvaluationContentSection flexDirection={"column"} gap={"0"}>
              <HeaderText fontSize="14px">2nd Notification</HeaderText>
              <ETAEvaluationContentRow>
                <SubHeaderText>
                  {formatTriggerDescription(item.secondNotification.triggerWhen, item.secondNotification.awayFrom)}
                </SubHeaderText>
              </ETAEvaluationContentRow>
              <ETAEvaluationContentRow>
                <SubHeaderText>
                  Time Window:{" "}
                  {formatTimeWindow(
                    item.secondNotification.timeWindowBeforeETA,
                    item.secondNotification.timeWindowAfterETA
                  )}
                </SubHeaderText>
              </ETAEvaluationContentRow>
            </ETAEvaluationContentSection>
          )}

          <ETAEvaluationContentSection>
            <HeaderText fontSize="14px">Follow Up</HeaderText>
            <SubHeaderText>{item.isFollowedUp ? "Yes" : "No"}</SubHeaderText>
          </ETAEvaluationContentSection>
        </ETAEvaluationContentContainer>
      </Collapse>
    </ETAEvaluationItemContainer>
  );
};
